<template>
    <div id="day-navigator-wrapper" class="d-flex w-100">
        <v-btn
            variant="text"
            icon
            @click="goToPreviousDay"
            :disabled="isPreviousDisabled"
        >
            <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <!-- Wrap date input in relative container -->
        <v-tooltip
            location="top"
            :disabled="!range"
            open-delay="100"
        >
            <template #activator="{ props: tooltipProps }">
                <div
                    class="date-input-wrapper"
                    v-bind="range ? tooltipProps : {}"
                >
                    <v-date-input
                        :model-value="modelValue"
                        @update:model-value="
                            (val) => $emit('update:modelValue', val)
                        "
                        :min="minDate"
                        :max="maxDate"
                        label="Date"
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        variant="solo"
                        multiple="range"
                        v-if="range"
                        style="width: 100%"
                    />
                    <v-date-input
                        :model-value="modelValue"
                        @update:model-value="
                            (val) => $emit('update:modelValue', val)
                        "
                        :min="minDate"
                        :max="maxDate"
                        label="Date"
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        variant="solo"
                        v-else
                        style="width: 100%"
                    />

                    <!-- Overlay custom display -->
                    <div
                        class="custom-date-overlay d-flex justify-space-between"
                    >
                        <span>
                            {{ displayValue }}
                        </span>
                        <v-btn
                            style="margin-top: -15px"
                            v-if="hasSelection"
                            icon
                            variant="text"
                            size="small"
                            @click="resetToToday"
                        >
                            <v-icon size="x-large">mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>
            </template>
            <span>You can select a single day or a date period.</span>
        </v-tooltip>

        <v-btn
            variant="text"
            icon
            @click="goToNextDay"
            :disabled="isNextDisabled"
        >
            <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
    </div>
</template>

<script>
import dayjs from "dayjs";

/**
 * @component DayNavigator
 * @description A reusable Vue component for navigating dates using left/right arrows.
 */
export default {
    name: "DayNavigator",
    props: {
        /**
         * The bound date or date range.
         * Single mode: Date object.
         * Range mode: Array of Date objects.
         * @type {Date|Array<Date>}
         */
        modelValue: {
            type: [Date, Array],
            required: true,
        },
        /**
         * Enables range selection mode.
         * @type {Boolean}
         * @default false
         */
        range: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        /**
         * Determines if a non-today selection exists.
         * Shows clear button only if:
         * - A date (or range) is selected AND
         * - It differs from today.
         *
         * @returns {boolean}
         */
        hasSelection() {
            const today = dayjs().startOf("day");

            if (this.range && Array.isArray(this.modelValue)) {
                return this.modelValue.some(
                    (date) => !dayjs(date).isSame(today, "day")
                );
            }

            if (this.modelValue instanceof Date) {
                return !dayjs(this.modelValue).isSame(today, "day");
            }

            return false;
        },
        /**
         * Computes the display string for the date or date range shown in the UI.
         *
         * - If in range mode (array of Date), and start date equals end date, returns a single formatted date.
         * - If in range mode and start ≠ end, returns a formatted "start - end" string.
         * - If in single mode (Date), returns the formatted single date.
         * - If no value is set, returns an empty string.
         *
         * @returns {string} The formatted date or date range string.
         */
        displayValue() {
            const options = {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            };

            if (Array.isArray(this.modelValue) && this.modelValue.length > 0) {
                const first = this.modelValue[0];
                const last = this.modelValue[this.modelValue.length - 1];

                if (first && last) {
                    if (first.getTime() === last.getTime()) {
                        return first.toLocaleDateString(undefined, options);
                    } else {
                        return `${first.toLocaleDateString(
                            undefined,
                            options
                        )} - ${last.toLocaleDateString(undefined, options)}`;
                    }
                }
            }
            if (this.modelValue instanceof Date) {
                return this.modelValue.toLocaleDateString(undefined, options);
            }
            return "";
        },
        /**
         * Minimum selectable date (local midnight). Uses today − 44 calendar days: the day that is
         * exactly 45 days ago is migrated out of RTDB, so it must not be the minimum or the picker
         * would offer an empty day.
         */
        minDate() {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const fortyFiveDaysAgo = new Date(today);
            fortyFiveDaysAgo.setDate(today.getDate() - 44);
            return fortyFiveDaysAgo;
        },
        maxDate() {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return today;
        },
        isPreviousDisabled() {
            const limit = this.minDate;
            if (this.range && Array.isArray(this.modelValue)) {
                const firstDate = new Date(this.modelValue[0]);
                return firstDate <= limit;
            }
            return this.modelValue <= limit;
        },
        isNextDisabled() {
            const limit = this.maxDate;
            if (this.range && Array.isArray(this.modelValue)) {
                const lastDate = new Date(
                    this.modelValue[this.modelValue.length - 1]
                );
                return lastDate >= limit;
            }
            return this.modelValue >= limit;
        },
    },
    methods: {
        /**
         * Resets the date selection to today's date.
         * Emits the update to parent.
         */
        resetToToday() {
            const today = dayjs().startOf("day").toDate();
            if (this.range) {
                this.$emit("update:modelValue", [today, today]);
            } else {
                this.$emit("update:modelValue", today);
            }
        },
        /**
         * Shifts the selected date or date range backward by one day.
         *
         * - If in range mode, creates an array of cloned Date objects, each moved one day earlier.
         *   @type {Array<Date>} shiftedDates - Array of new Date objects, each one day earlier.
         *
         * - If in single mode, clones the Date object and moves it one day earlier.
         *   @type {Date} clonedDate - Single new Date object, one day earlier.
         *
         * Emits the updated value back to the parent (via `update:modelValue`).
         * Does not mutate the original modelValue.
         */
        goToPreviousDay() {
            if (this.isPreviousDisabled) return;

            if (this.range && Array.isArray(this.modelValue)) {
                const shiftedDates = this.modelValue.map((originalDate) =>
                    dayjs(originalDate).subtract(1, "day").toDate()
                );
                this.$emit("update:modelValue", shiftedDates);
            } else {
                const shiftedDate = dayjs(this.modelValue)
                    .subtract(1, "day")
                    .toDate();
                this.$emit("update:modelValue", shiftedDate);
            }
        },

        /**
         * Shifts the selected date or date range forward by one day.
         *
         * - If in range mode, creates an array of cloned Date objects, each moved one day later.
         *   @type {Array<Date>} shiftedDates - Array of new Date objects, each one day later.
         *
         * - If in single mode, clones the Date object and moves it one day later.
         *   @type {Date} clonedDate - Single new Date object, one day later.
         *
         * Emits the updated value back to the parent (via `update:modelValue`).
         * Does not mutate the original modelValue.
         */
        goToNextDay() {
            if (this.isNextDisabled) return;

            if (this.range && Array.isArray(this.modelValue)) {
                const shiftedDates = this.modelValue.map((originalDate) =>
                    dayjs(originalDate).add(1, "day").toDate()
                );
                this.$emit("update:modelValue", shiftedDates);
            } else {
                const shiftedDate = dayjs(this.modelValue)
                    .add(1, "day")
                    .toDate();
                this.$emit("update:modelValue", shiftedDate);
            }
        },
    },
};
</script>
<style scoped>
.date-input-wrapper {
    position: relative;
    width: 100%;
}

.custom-date-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding-left: 42px;
    pointer-events: none;
    color: rgba(0, 0, 0, 0.87);
    font-size: 16px;
    font-weight: 400;
}

.custom-date-overlay > * {
    pointer-events: auto; /* allow X button to be clickable */
}

.date-input-wrapper:deep(.v-field__input) {
  color: transparent !important;
}
</style>
